// src/pages/provider/ReportsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Plus,
  Send,
  AlertCircle,
  Clock,
  Building2,
  Phone,
  Mail,
  User,
  DollarSign,
  Lightbulb,
  Bug,
  HelpCircle,
  Shield,
  FileText,
  MoreHorizontal,
  CheckCircle,
  Clock as ClockIcon,
  AlertTriangle,
  XCircle,
  ChevronRight,
  Calendar,
  Flag,
  Layers,
  RefreshCw,
  MessageCircle,
  UserCheck,
  ChevronLeft,
  Star
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/Report.module.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProviderReports,
  createProviderReport,
  getReportForProvider,
  providerReply,
  addFeedback,
  selectMyReports,
  selectProviderReport,
  selectReportPagination,
  selectMyReportsLoading,
  selectReportSubmitting,
  selectSubmitReportError,
  selectSubmitReportSuccess,
  selectReplySuccess,
  selectFeedbackSuccess,
  clearReportErrors,
  clearReportSuccess,
  resetReportState
} from '../../store/slices/reportSlice';
import {
  selectProfile
} from '../../store/slices/profileSlice';

const ReportsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { currentLanguage } = useLanguage();
  
  // Redux state - Profile
  const profile = useSelector(selectProfile);
  
  // Redux state - Reports
  const myReports = useSelector(selectMyReports);
  const providerReport = useSelector(selectProviderReport);
  const pagination = useSelector(selectReportPagination);
  const loading = useSelector(selectMyReportsLoading);
  const submitting = useSelector(selectReportSubmitting);
  const submitError = useSelector(selectSubmitReportError);
  const submitSuccess = useSelector(selectSubmitReportSuccess);
  const replySuccess = useSelector(selectReplySuccess);
  const feedbackSuccess = useSelector(selectFeedbackSuccess);
  
  // Get provider ID from profile
  const providerId = profile?.providerProfile?._id || profile?._id || user?._id || user?.id;
  const providerBusinessName = profile?.providerProfile?.businessName || profile?.name || '';
  
  // Local state
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showNewReportModal, setShowNewReportModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [selectedReport, setSelectedReport] = useState(null);
  
  const [newReport, setNewReport] = useState({
    type: '',
    subject: '',
    description: '',
    severity: 'medium',
    category: 'technical',
    stepsToReproduce: [],
    expectedBehavior: '',
    actualBehavior: ''
  });

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: reportsTitle } = useTranslation('Reports');
  const { translatedText: searchReports } = useTranslation('Search reports...');
  const { translatedText: all } = useTranslation('All');
  const { translatedText: pending } = useTranslation('Pending');
  const { translatedText: underReview } = useTranslation('Under Review');
  const { translatedText: inProgress } = useTranslation('In Progress');
  const { translatedText: resolved } = useTranslation('Resolved');
  const { translatedText: dismissed } = useTranslation('Dismissed');
  const { translatedText: reportsFound } = useTranslation('reports found');
  const { translatedText: noReportsFound } = useTranslation('No reports found');
  const { translatedText: submitReportIfIssue } = useTranslation('Submit a report if you encounter any issues');
  const { translatedText: haitianServices } = useTranslation('Haitian Services Marketplace');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('Made with ❤️ in Haiti');
  const { translatedText: refresh } = useTranslation('Refresh');
  const { translatedText: loadingReports } = useTranslation('Loading reports...');
  const { translatedText: viewDetails } = useTranslation('View Details');
  const { translatedText: reply } = useTranslation('Reply');
  const { translatedText: provideFeedback } = useTranslation('Provide Feedback');
  const { translatedText: noReportsDesc } = useTranslation('You haven\'t submitted any reports yet');
  const { translatedText: providerNotFound } = useTranslation('Provider information not found. Please log in again.');
  const { translatedText: assignedTo } = useTranslation('Assigned To');
  const { translatedText: adminResponse } = useTranslation('Admin Response');
  const { translatedText: noResponseYet } = useTranslation('No response yet');
  const { translatedText: status } = useTranslation('Status');
  const { translatedText: type } = useTranslation('Type');
  const { translatedText: severity } = useTranslation('Severity');
  const { translatedText: createdAt } = useTranslation('Created At');
  const { translatedText: updatedAt } = useTranslation('Updated At');
  const { translatedText: priority } = useTranslation('Priority');
  const { translatedText: normal } = useTranslation('Normal');
  const { translatedText: urgent } = useTranslation('Urgent');
  const { translatedText: close } = useTranslation('Close');
  const { translatedText: back } = useTranslation('Back');
  
  // Modal Translations
  const { translatedText: submitReport } = useTranslation('Submit Report');
  const { translatedText: reportType } = useTranslation('Report Type *');
  const { translatedText: subject } = useTranslation('Subject *');
  const { translatedText: subjectPlaceholder } = useTranslation('Brief subject of the report');
  const { translatedText: descriptionLabel } = useTranslation('Description *');
  const { translatedText: descriptionPlaceholder } = useTranslation('Please describe the issue in detail');
  const { translatedText: submitButton } = useTranslation('Submit Report');
  const { translatedText: stepsToReproduceLabel } = useTranslation('Steps to Reproduce');
  const { translatedText: expectedBehavior } = useTranslation('Expected Behavior');
  const { translatedText: actualBehavior } = useTranslation('Actual Behavior');
  
  // Alert Translations
  const { translatedText: error } = useTranslation('Error');
  const { translatedText: fillAllFields } = useTranslation('Please fill in all required fields');
  const { translatedText: reportSubmitted } = useTranslation('Report Submitted');
  const { translatedText: reportSubmittedMessage } = useTranslation('Thank you for your report. Our support team will review it shortly.');
  const { translatedText: ok } = useTranslation('OK');
  const { translatedText: replySent } = useTranslation('Reply Sent');
  const { translatedText: replySentMessage } = useTranslation('Your reply has been sent successfully');
  const { translatedText: feedbackSubmitted } = useTranslation('Feedback Submitted');
  const { translatedText: feedbackSubmittedMessage } = useTranslation('Thank you for your feedback');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboardLabel } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // Report Types - map to match API data
  const appIssue = useTranslation('App Issue').translatedText;
  const paymentIssue = useTranslation('Payment Issue').translatedText;
  const accountIssue = useTranslation('Account Issue').translatedText;
  const featureRequest = useTranslation('Feature Request').translatedText;
  const bugReport = useTranslation('Bug Report').translatedText;
  const supportRequest = useTranslation('Support Request').translatedText;
  const securityConcern = useTranslation('Security Concern').translatedText;
  const contentIssue = useTranslation('Content Issue').translatedText;
  const other = useTranslation('Other').translatedText;

  const reportTypes = [
    { id: 'app_issue', label: appIssue, icon: Phone },
    { id: 'payment_issue', label: paymentIssue, icon: DollarSign },
    { id: 'account_issue', label: accountIssue, icon: User },
    { id: 'feature_request', label: featureRequest, icon: Lightbulb },
    { id: 'bug_report', label: bugReport, icon: Bug },
    { id: 'support_request', label: supportRequest, icon: HelpCircle },
    { id: 'security_concern', label: securityConcern, icon: Shield },
    { id: 'content_issue', label: contentIssue, icon: FileText },
    { id: 'other', label: other, icon: MoreHorizontal }
  ];

  // Severity options
  const low = useTranslation('Low').translatedText;
  const medium = useTranslation('Medium').translatedText;
  const high = useTranslation('High').translatedText;
  const critical = useTranslation('Critical').translatedText;

  const severityOptions = [
    { id: 'low', label: low, color: colors.success },
    { id: 'medium', label: medium, color: colors.warning },
    { id: 'high', label: high, color: colors.error },
    { id: 'critical', label: critical, color: colors.primary }
  ];

  const statusFilters = [
    { id: 'all', label: all },
    { id: 'pending', label: pending },
    { id: 'under_review', label: underReview },
    { id: 'in_progress', label: inProgress },
    { id: 'resolved', label: resolved },
    { id: 'dismissed', label: dismissed }
  ];

  // Fetch reports on mount and filter changes
  useEffect(() => {
    if (providerId) {
      fetchMyReports();
    }
  }, [currentPage, providerId]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      dispatch(clearReportErrors());
      dispatch(clearReportSuccess());
    };
  }, [dispatch]);

  // Handle success states
  useEffect(() => {
    if (submitSuccess) {
      alert(`${reportSubmitted}\n\n${reportSubmittedMessage}`);
      setShowNewReportModal(false);
      resetNewReportForm();
      dispatch(clearReportSuccess());
      fetchMyReports();
    }
  }, [submitSuccess]);

  useEffect(() => {
    if (replySuccess) {
      alert(`${replySent}\n\n${replySentMessage}`);
      setShowReplyModal(false);
      setReplyMessage('');
      dispatch(clearReportSuccess());
      fetchMyReports();
    }
  }, [replySuccess]);

  useEffect(() => {
    if (feedbackSuccess) {
      alert(`${feedbackSubmitted}\n\n${feedbackSubmittedMessage}`);
      setShowFeedbackModal(false);
      setFeedbackComment('');
      setFeedbackRating(5);
      dispatch(clearReportSuccess());
      fetchMyReports();
    }
  }, [feedbackSuccess]);

  // Show error toast
  useEffect(() => {
    if (submitError) {
      alert(`${error}\n\n${submitError}`);
      dispatch(clearReportErrors());
    }
  }, [submitError]);

  const fetchMyReports = () => {
    if (!providerId) {
      console.warn('No provider ID available');
      return;
    }

    const params = {
      page: currentPage,
      limit: limit,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    dispatch(getProviderReports(params));
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchMyReports();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return colors.warning;
      case 'under_review': return colors.secondary;
      case 'in_progress': return colors.primary;
      case 'resolved': return colors.success;
      case 'dismissed': return colors.error;
      default: return colors.gray400;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'pending': return colors.warning + '20';
      case 'under_review': return colors.secondary + '20';
      case 'in_progress': return colors.primary + '20';
      case 'resolved': return colors.success + '20';
      case 'dismissed': return colors.error + '20';
      default: return colors.gray200;
    }
  };

  const getStatusLabel = (status) => {
    const found = statusFilters.find(f => f.id === status);
    return found ? found.label : status;
  };

  const getTypeIcon = (type) => {
    const found = reportTypes.find(t => t.id === type);
    return found ? found.icon : AlertCircle;
  };

  const getTypeLabel = (type) => {
    const found = reportTypes.find(t => t.id === type);
    return found ? found.label : type;
  };

  const getSeverityColor = (severity) => {
    const found = severityOptions.find(s => s.id === severity);
    return found ? found.color : colors.gray400;
  };

  const getSeverityLabel = (severity) => {
    const found = severityOptions.find(s => s.id === severity);
    return found ? found.label : severity;
  };

  const resetNewReportForm = () => {
    setNewReport({
      type: '',
      subject: '',
      description: '',
      severity: 'medium',
      category: 'technical',
      stepsToReproduce: [],
      expectedBehavior: '',
      actualBehavior: ''
    });
  };

  const filteredReports = myReports.filter(report => {
    const matchesSearch = 
      report.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.type?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = selectedFilter === 'all' || report.status === selectedFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleSubmitReport = () => {
    if (!newReport.type || !newReport.subject || !newReport.description) {
      alert(`${error}\n\n${fillAllFields}`);
      return;
    }

    const providerIdFromProfile = profile?.providerProfile?._id || profile?._id;
    
    if (!providerIdFromProfile) {
      alert(`${error}\n\n${providerNotFound}`);
      return;
    }

    const reportData = {
      type: newReport.type,
      category: newReport.category || 'technical',
      severity: newReport.severity || 'medium',
      subject: newReport.subject,
      description: newReport.description,
      stepsToReproduce: newReport.stepsToReproduce || [],
      expectedBehavior: newReport.expectedBehavior || '',
      actualBehavior: newReport.actualBehavior || '',
      providerId: providerIdFromProfile,
      providerDetails: {
        businessName: profile?.providerProfile?.businessName || profile?.name || '',
        serviceType: profile?.providerProfile?.serviceType || '',
        phone: profile?.phone || '',
        email: profile?.email || ''
      }
    };

    dispatch(createProviderReport(reportData));
  };

  const handleReply = () => {
    if (!replyMessage.trim()) {
      alert(`${error}\n\nPlease enter a reply message`);
      return;
    }

    dispatch(providerReply({
      reportId: selectedReportId,
      message: replyMessage
    }));
  };

  const handleFeedback = () => {
    dispatch(addFeedback({
      reportId: selectedReportId,
      rating: feedbackRating,
      comment: feedbackComment
    }));
  };

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setSelectedReportId(report._id || report.id);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedReport(null);
    setSelectedReportId(null);
  };

  const handleOpenReply = (reportId) => {
    setSelectedReportId(reportId);
    setShowReplyModal(true);
    setReplyMessage('');
  };

  const handleOpenFeedback = (reportId) => {
    setSelectedReportId(reportId);
    setShowFeedbackModal(true);
    setFeedbackRating(5);
    setFeedbackComment('');
  };

  const renderReportItem = (item) => {
    const Icon = getTypeIcon(item.type);
    const statusColor = getStatusColor(item.status);
    const badgeColor = getStatusBadgeColor(item.status);
    const severityColor = getSeverityColor(item.severity);
    const isResolved = item.status === 'resolved';
    const canFeedback = isResolved && !item.feedback;
    
    // Get provider details from the report data
    const providerName = item.providerDetails?.businessName || 'Provider';
    const providerPhone = item.providerDetails?.phone || '';
    const providerEmail = item.providerDetails?.email || '';
    
    // Get admin response if available
    const hasAdminResponse = item.adminResponse && item.adminResponse.message;
    const adminMessage = item.adminResponse?.message || '';
    const adminName = item.assignedTo?.name || 'Admin';
    const adminStatus = item.adminResponse?.status || '';
    const resolvedAt = item.adminResponse?.resolvedAt || '';
    
    return (
      <div key={item._id || item.id} className={styles.reportItem}>
        <div className={styles.reportItemBody}>
          <div className={styles.reportHeader}>
            <div className={styles.reportType}>
              <Icon size={20} color={colors.primary} />
              <span className={styles.reportTypeText}>{getTypeLabel(item.type)}</span>
            </div>
            <div className={styles.reportStatus} style={{ backgroundColor: badgeColor, color: statusColor }}>
              {getStatusLabel(item.status)}
            </div>
          </div>

          <div className={styles.reportSubject}>{item.subject}</div>
          <div className={styles.reportDescription}>
            {item.description}
          </div>

          {/* Provider Info */}
          <div className={styles.providerInfoSection}>
            <div className={styles.providerInfoRow}>
              <Building2 size={14} color={colors.gray500} />
              <span className={styles.providerInfoText}>{providerName}</span>
            </div>
            {providerPhone && (
              <div className={styles.providerInfoRow}>
                <Phone size={14} color={colors.gray500} />
                <span className={styles.providerInfoText}>{providerPhone}</span>
              </div>
            )}
            {providerEmail && (
              <div className={styles.providerInfoRow}>
                <Mail size={14} color={colors.gray500} />
                <span className={styles.providerInfoText}>{providerEmail}</span>
              </div>
            )}
          </div>

          {/* Admin Response */}
          {hasAdminResponse && (
            <div className={styles.adminResponseSection}>
              <div className={styles.adminResponseHeader}>
                <UserCheck size={14} color={colors.primary} />
                <span className={styles.adminResponseLabel}>{adminResponse}</span>
                <span className={styles.adminName}>- {adminName}</span>
              </div>
              <div className={styles.adminResponseMessage}>
                "{adminMessage}"
              </div>
              {resolvedAt && (
                <div className={styles.adminResponseMeta}>
                  <Clock size={12} color={colors.gray400} />
                  <span className={styles.adminResponseDate}>
                    {new Date(resolvedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Assigned To */}
          {item.assignedTo && !hasAdminResponse && (
            <div className={styles.assignedSection}>
              <UserCheck size={14} color={colors.gray500} />
              <span className={styles.assignedText}>
                {assignedTo}: {item.assignedTo.name}
              </span>
            </div>
          )}

          <div className={styles.reportFooter}>
            <div className={styles.reportMeta}>
              <div className={styles.severityBadge} style={{ backgroundColor: severityColor + '20' }}>
                <AlertCircle size={12} color={severityColor} />
                <span className={styles.severityText} style={{ color: severityColor }}>
                  {getSeverityLabel(item.severity)}
                </span>
              </div>
              {item.priority && (
                <div className={styles.priorityBadge} style={{ 
                  backgroundColor: item.priority === 'urgent' ? colors.error + '20' : colors.gray200 
                }}>
                  <Flag size={12} color={item.priority === 'urgent' ? colors.error : colors.gray500} />
                  <span className={styles.priorityText} style={{ 
                    color: item.priority === 'urgent' ? colors.error : colors.gray500 
                  }}>
                    {item.priority === 'urgent' ? urgent : normal}
                  </span>
                </div>
              )}
              <div className={styles.reportDate}>
                <Clock size={14} />
                <span>{new Date(item.createdAt).toLocaleDateString()}</span>
              </div>
              {item.communication && item.communication.length > 0 && (
                <div className={styles.communicationCount}>
                  <MessageCircle size={14} />
                  <span>{item.communication.length}</span>
                </div>
              )}
            </div>
            <div className={styles.reportActions}>
              <button 
                className={styles.viewBtn}
                onClick={() => handleViewDetails(item)}
              >
                {viewDetails}
              </button>
              {item.status !== 'resolved' && item.status !== 'dismissed' && (
                <button 
                  className={styles.replyBtn}
                  onClick={() => handleOpenReply(item._id || item.id)}
                >
                  {reply}
                </button>
              )}
              {canFeedback && (
                <button 
                  className={styles.feedbackBtn}
                  onClick={() => handleOpenFeedback(item._id || item.id)}
                >
                  {provideFeedback}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render report details modal
  const renderDetailsModal = () => {
    if (!selectedReport) return null;

    const Icon = getTypeIcon(selectedReport.type);
    const statusColor = getStatusColor(selectedReport.status);
    const badgeColor = getStatusBadgeColor(selectedReport.status);
    const severityColor = getSeverityColor(selectedReport.severity);
    const isResolved = selectedReport.status === 'resolved';
    const canFeedback = isResolved && !selectedReport.feedback;
    
    const providerName = selectedReport.providerDetails?.businessName || 'Provider';
    const providerPhone = selectedReport.providerDetails?.phone || '';
    const providerEmail = selectedReport.providerDetails?.email || '';
    
    const hasAdminResponse = selectedReport.adminResponse && selectedReport.adminResponse.message;
    const adminMessage = selectedReport.adminResponse?.message || '';
    const adminName = selectedReport.assignedTo?.name || 'Admin';
    const resolvedAt = selectedReport.adminResponse?.resolvedAt || '';

    // Get communication history
    const communications = selectedReport.communication || [];

    return (
      <div 
        className={styles.modalOverlay} 
        onClick={handleCloseDetailsModal}
      >
        <div 
          className={styles.detailsModalContent} 
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.modalHeader}>
            <div className={styles.detailsModalTitle}>
              <button 
                className={styles.backBtn}
                onClick={handleCloseDetailsModal}
              >
                <ChevronLeft size={20} />
                <span>{back}</span>
              </button>
              <h2>Report Details</h2>
            </div>
            <button 
              className={styles.modalClose} 
              onClick={handleCloseDetailsModal}
            >
              <X size={24} />
            </button>
          </div>

          <div className={styles.detailsModalBody}>
            {/* Status Banner */}
            <div className={styles.detailsStatusBanner} style={{ backgroundColor: badgeColor }}>
              <span className={styles.detailsStatusLabel} style={{ color: statusColor }}>
                Status: {getStatusLabel(selectedReport.status)}
              </span>
              <span className={styles.detailsStatusDate}>
                <Clock size={14} />
                {new Date(selectedReport.createdAt).toLocaleString()}
              </span>
            </div>

            {/* Report Header */}
            <div className={styles.detailsHeader}>
              <div className={styles.detailsTypeBadge}>
                <Icon size={20} color={colors.primary} />
                <span>{getTypeLabel(selectedReport.type)}</span>
              </div>
              <div className={styles.detailsSeverityBadge} style={{ backgroundColor: severityColor + '20' }}>
                <AlertCircle size={14} color={severityColor} />
                <span style={{ color: severityColor }}>{getSeverityLabel(selectedReport.severity)}</span>
              </div>
            </div>

            {/* Subject */}
            <h3 className={styles.detailsSubject}>{selectedReport.subject}</h3>

            {/* Description */}
            <div className={styles.detailsSection}>
              <h4 className={styles.detailsSectionTitle}>Description</h4>
              <p className={styles.detailsDescription}>{selectedReport.description}</p>
            </div>

            {/* Provider Info */}
            <div className={styles.detailsSection}>
              <h4 className={styles.detailsSectionTitle}>Provider Information</h4>
              <div className={styles.detailsInfoGrid}>
                <div className={styles.detailsInfoItem}>
                  <Building2 size={16} color={colors.gray500} />
                  <span>{providerName}</span>
                </div>
                {providerPhone && (
                  <div className={styles.detailsInfoItem}>
                    <Phone size={16} color={colors.gray500} />
                    <span>{providerPhone}</span>
                  </div>
                )}
                {providerEmail && (
                  <div className={styles.detailsInfoItem}>
                    <Mail size={16} color={colors.gray500} />
                    <span>{providerEmail}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Admin Response */}
            <div className={styles.detailsSection}>
              <h4 className={styles.detailsSectionTitle}>{adminResponse}</h4>
              {hasAdminResponse ? (
                <div className={styles.detailsAdminResponse}>
                  <div className={styles.detailsAdminHeader}>
                    <UserCheck size={16} color={colors.primary} />
                    <span className={styles.detailsAdminName}>{adminName}</span>
                    {resolvedAt && (
                      <span className={styles.detailsAdminDate}>
                        {new Date(resolvedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <p className={styles.detailsAdminMessage}>"{adminMessage}"</p>
                </div>
              ) : (
                <p className={styles.detailsNoResponse}>{noResponseYet}</p>
              )}
            </div>

            {/* Assigned To */}
            {selectedReport.assignedTo && !hasAdminResponse && (
              <div className={styles.detailsSection}>
                <h4 className={styles.detailsSectionTitle}>{assignedTo}</h4>
                <div className={styles.detailsInfoItem}>
                  <UserCheck size={16} color={colors.gray500} />
                  <span>{selectedReport.assignedTo.name}</span>
                </div>
              </div>
            )}

            {/* Communication History */}
            {communications.length > 0 && (
              <div className={styles.detailsSection}>
                <h4 className={styles.detailsSectionTitle}>
                  <MessageCircle size={16} />
                  Communication History ({communications.length})
                </h4>
                <div className={styles.detailsCommunicationList}>
                  {communications.map((comm, index) => (
                    <div key={index} className={styles.detailsCommunicationItem}>
                      <div className={styles.detailsCommHeader}>
                        <span className={styles.detailsCommSender}>
                          {comm.sender === 'admin' ? 'Admin' : providerName}
                        </span>
                        <span className={styles.detailsCommDate}>
                          {new Date(comm.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className={styles.detailsCommMessage}>{comm.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta Info */}
            <div className={styles.detailsMetaGrid}>
              <div className={styles.detailsMetaItem}>
                <span className={styles.detailsMetaLabel}>{status}</span>
                <span className={styles.detailsMetaValue} style={{ color: statusColor }}>
                  {getStatusLabel(selectedReport.status)}
                </span>
              </div>
              <div className={styles.detailsMetaItem}>
                <span className={styles.detailsMetaLabel}>{type}</span>
                <span className={styles.detailsMetaValue}>{getTypeLabel(selectedReport.type)}</span>
              </div>
              <div className={styles.detailsMetaItem}>
                <span className={styles.detailsMetaLabel}>{severity}</span>
                <span className={styles.detailsMetaValue} style={{ color: severityColor }}>
                  {getSeverityLabel(selectedReport.severity)}
                </span>
              </div>
              <div className={styles.detailsMetaItem}>
                <span className={styles.detailsMetaLabel}>{updatedAt}</span>
                <span className={styles.detailsMetaValue}>
                  {new Date(selectedReport.updatedAt).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.detailsActions}>
              {selectedReport.status !== 'resolved' && selectedReport.status !== 'dismissed' && (
                <button 
                  className={styles.detailsReplyBtn}
                  onClick={() => {
                    handleCloseDetailsModal();
                    setTimeout(() => handleOpenReply(selectedReport._id || selectedReport.id), 100);
                  }}
                >
                  <Send size={18} />
                  {reply}
                </button>
              )}
              {canFeedback && (
                <button 
                  className={styles.detailsFeedbackBtn}
                  onClick={() => {
                    handleCloseDetailsModal();
                    setTimeout(() => handleOpenFeedback(selectedReport._id || selectedReport.id), 100);
                  }}
                >
                  <Star size={18} />
                  {provideFeedback}
                </button>
              )}
              <button 
                className={styles.detailsCloseBtn}
                onClick={handleCloseDetailsModal}
              >
                {close}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check if provider profile is missing
  if (!profile) {
    return (
      <div className={styles.emptyContainer}>
        <AlertCircle size={48} color={colors.error} />
        <h5>Provider Not Found</h5>
        <p>{providerNotFound || 'Provider information not found. Please log in again.'}</p>
        <button 
          className={styles.retryBtn}
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading && myReports.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{loadingReports}</p>
      </div>
    );
  }

  return (
    <div className={styles.reportsContainer}>
      {/* Header */}
      <div className={styles.reportsHeader}>
        <div className={styles.headerLeft}>
          <h2>{reportsTitle}</h2>
          {providerBusinessName && (
            <span className={styles.providerBadge}>
              <Building2 size={14} />
              {providerBusinessName}
            </span>
          )}
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.refreshBtn}
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? styles.spinning : ''} />
            {refresh}
          </button>
          <button 
            className={styles.newReportBtn}
            onClick={() => setShowNewReportModal(true)}
          >
            <Plus size={18} />
            {submitReport}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className={styles.searchContainer}>
        <div className={styles.searchBar}>
          <Search size={20} color={colors.gray400} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={searchReports}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.length > 0 && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <X size={20} color={colors.gray400} />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersContainer}>
        {statusFilters.map((filter) => (
          <button
            key={filter.id}
            className={`${styles.filterChip} ${selectedFilter === filter.id ? styles.active : ''}`}
            onClick={() => setSelectedFilter(filter.id)}
          >
            {filter.label}
            {filter.id !== 'all' && (
              <span className={styles.filterCount}>
                {myReports.filter(r => r.status === filter.id).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Results Count */}
      <div className={styles.resultsHeader}>
        <span className={styles.resultsText}>
          {filteredReports.length} {reportsFound}
        </span>
        {pagination && pagination.total > 0 && (
          <span className={styles.totalText}>
            (Total: {pagination.total})
          </span>
        )}
      </div>

      {/* Reports List */}
      <div className={styles.reportsList}>
        {filteredReports.length > 0 ? (
          filteredReports.map(renderReportItem)
        ) : (
          <div className={styles.emptyContainer}>
            <FileText size={64} color={colors.gray300} />
            <h5 className={styles.emptyTitle}>{noReportsFound}</h5>
            <p className={styles.emptySubtitle}>{noReportsDesc || submitReportIfIssue}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {pagination.pages || 1}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.pages || 1))}
            disabled={currentPage === (pagination.pages || 1) || loading}
          >
            Next
          </button>
        </div>
      )}

      {/* New Report Modal */}
      {showNewReportModal && (
        <div className={styles.modalOverlay} onClick={() => setShowNewReportModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{submitReport}</h2>
              <button className={styles.modalClose} onClick={() => setShowNewReportModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.providerInfoBar}>
                <Building2 size={16} />
                <span>{providerBusinessName || 'Provider'}</span>
              </div>

              <div className={styles.modalField}>
                <label className={styles.modalLabel}>{reportType}</label>
                <div className={styles.typeGrid}>
                  {reportTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        className={`${styles.typeOption} ${newReport.type === type.id ? styles.active : ''}`}
                        onClick={() => setNewReport({ ...newReport, type: type.id })}
                      >
                        <Icon size={16} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.modalField}>
                <label className={styles.modalLabel}>{severity}</label>
                <div className={styles.severityContainer}>
                  {severityOptions.map((sev) => (
                    <button
                      key={sev.id}
                      className={`${styles.severityOption} ${newReport.severity === sev.id ? styles.active : ''}`}
                      onClick={() => setNewReport({ ...newReport, severity: sev.id })}
                    >
                      <span style={{ color: newReport.severity === sev.id ? 'white' : sev.color }}>
                        {sev.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.modalField}>
                <label className={styles.modalLabel}>{subject}</label>
                <input
                  type="text"
                  className={styles.modalInput}
                  placeholder={subjectPlaceholder}
                  value={newReport.subject}
                  onChange={(e) => setNewReport({ ...newReport, subject: e.target.value })}
                />
              </div>

              <div className={styles.modalField}>
                <label className={styles.modalLabel}>{descriptionLabel}</label>
                <textarea
                  className={styles.modalTextarea}
                  placeholder={descriptionPlaceholder}
                  value={newReport.description}
                  onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                  rows={4}
                />
              </div>

              <button
                className={styles.submitBtn}
                onClick={handleSubmitReport}
                disabled={submitting || !providerId}
              >
                {submitting ? (
                  <div className={styles.spinnerSmall} />
                ) : (
                  <>
                    <Send size={20} />
                    <span>{submitButton}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && (
        <div className={styles.modalOverlay} onClick={() => setShowReplyModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{reply}</h2>
              <button className={styles.modalClose} onClick={() => setShowReplyModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalField}>
                <label className={styles.modalLabel}>Message</label>
                <textarea
                  className={styles.modalTextarea}
                  placeholder="Write your reply..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={4}
                />
              </div>
              <button
                className={styles.submitBtn}
                onClick={handleReply}
                disabled={submitting}
              >
                {submitting ? (
                  <div className={styles.spinnerSmall} />
                ) : (
                  <>
                    <Send size={20} />
                    <span>Send Reply</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className={styles.modalOverlay} onClick={() => setShowFeedbackModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{provideFeedback}</h2>
              <button className={styles.modalClose} onClick={() => setShowFeedbackModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalField}>
                <label className={styles.modalLabel}>Rating</label>
                <div className={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`${styles.starBtn} ${feedbackRating >= star ? styles.active : ''}`}
                      onClick={() => setFeedbackRating(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.modalField}>
                <label className={styles.modalLabel}>Comment (Optional)</label>
                <textarea
                  className={styles.modalTextarea}
                  placeholder="Share your experience with how your report was handled..."
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  rows={3}
                />
              </div>
              <button
                className={styles.submitBtn}
                onClick={handleFeedback}
                disabled={submitting}
              >
                {submitting ? (
                  <div className={styles.spinnerSmall} />
                ) : (
                  <>
                    <Send size={20} />
                    <span>Submit Feedback</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Details Modal */}
      {showDetailsModal && renderDetailsModal()}

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboardLabel}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReportsPage;